import { IconRefresh } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useCargarCfdi } from "@/features/cargar-cfdi/hooks/use-cargar-cfdi";
import { useSatValidationFlow } from "@/features/cargar-cfdi/hooks/use-sat-validation-flow";
import type { SatError } from "@/features/cargar-cfdi/types";
import { validarParejaCfdi } from "@/features/cargar-cfdi/utils/validar-pareja";
import { documentosApi } from "@/features/documentos/services/documentos.api";
import { useEsDueno } from "@/features/solicitudes/hooks/use-es-dueno";
import { useSolicitud } from "@/features/solicitudes/hooks/use-solicitud";
import { Button, Group, PageHeader, ReadOnlyBanner, Stack } from "@/components/ui";
import { LayoutConStepper, PASO_PAGE_SHELL } from "@/components/ui/solicitud-stepper";
import { estaPostEnvio } from "@/lib/estado-solicitud";
import { toastAdvertencia, toastExito } from "@/lib/toast";
import { qk } from "@/lib/query-keys";
import type { CargarCFDIResultado } from "@/lib/types";
import { Route } from "@/routes/_authed-tw/solicitudes/$id/cargar-cfdi";
import { extractApiMessage } from "@/shared/api/extract-message";
import { CFDICargadoCard } from "./CFDICargadoCard";
import { CFDIPdfDropzone } from "./CFDIPdfDropzone";
import { ReglasRechazadasCard } from "./ReglasRechazadasCard";
import { SatErrorCard } from "./SatErrorCard";
import { SatValidacionProgreso } from "./SatValidacionProgreso";
import { VentanasPuePpdBanner } from "./VentanasPuePpdBanner";

/**
 * Container · maneja el flujo dual XML+PDF:
 *
 *   1. El usuario elige XML + PDF (pareja con mismo nombre base).
 *   2. Validación local (extensión, header PDF, XML CFDI 4.0, nombres
 *      coinciden) en `CFDIPdfDropzone`.
 *   3. Al pulsar "Validar CFDI" se sube el XML al endpoint existente
 *      (POST /api/solicitudes/{id}/cfdi). Esto dispara reglas RN1-RN10.
 *   4. Si reglas pasan, polling SAT.
 *   5. Cuando SAT marca Validado, se sube el PDF como documento de
 *      categoría FacturaPdf (POST /api/solicitudes/{id}/documentos).
 *   6. Tras subir PDF, navega a /datos-generales.
 *
 * Se preserva el PDF en estado local (`pdfPendienteRef`) durante todo el
 * proceso porque el endpoint actual de CFDI solo acepta XML.
 */
export function CargarCFDIPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: sol } = useSolicitud(id);

  const [resultado, setResultado] = useState<CargarCFDIResultado | null>(null);
  const [errorSat, setErrorSat] = useState<SatError | null>(null);
  // XML y PDF retenidos en memoria hasta que SAT valide · entonces se suben ambos.
  const xmlPendienteRef = useRef<File | null>(null);
  const pdfPendienteRef = useRef<File | null>(null);
  // Input múltiple oculto para el botón "Reemplazar CFDI" (abre el diálogo
  // directo · sin mostrar el dropzone).
  const reemplazarInputRef = useRef<HTMLInputElement>(null);

  // Sube XML como FacturaXml y PDF como FacturaPdf en paralelo.
  // Si alguno falla, muestra aviso orange pero no bloquea el flujo.
  const subirDocumentos = useMutation({
    mutationFn: async ({ xml, pdf }: { xml: File | null; pdf: File | null }) => {
      const tareas = [];
      if (xml) tareas.push(documentosApi.subir(id, 1, "FacturaXml" as const, xml));
      if (pdf) tareas.push(documentosApi.subir(id, 1, "FacturaPdf" as const, pdf));
      await Promise.all(tareas);
    },
    onSuccess: () => {
      toastExito({ title: "Documentos cargados", message: "El XML y PDF del CFDI quedaron asociados como soporte documental." });
      void qc.invalidateQueries({ queryKey: qk.solicitud(id) });
      void qc.invalidateQueries({ queryKey: qk.documentos(id) });
      navigate({ to: "/solicitudes/$id/datos-generales", params: { id }, replace: true });
    },
    onError: (e) => {
      // El CFDI ya quedó validado en backend · solo falló el upload de documentos.
      // No bloqueamos el flujo: el usuario puede subirlos manualmente en el paso Documentos.
      toastAdvertencia({
        title: "Los documentos no se pudieron subir automáticamente",
        message: extractApiMessage(e) + " · Continúa con la solicitud · podrás subirlos en el paso Documentos.",
      });
      navigate({ to: "/solicitudes/$id/datos-generales", params: { id }, replace: true });
    },
  });

  const sat = useSatValidationFlow(id, {
    onValidado: () => {
      // SAT vigente · subimos XML y PDF retenidos.
      const xml = xmlPendienteRef.current;
      const pdf = pdfPendienteRef.current;
      if (xml || pdf) {
        subirDocumentos.mutate({ xml, pdf });
      } else {
        navigate({ to: "/solicitudes/$id/datos-generales", params: { id }, replace: true });
      }
    },
    onErrorSat: (err) => {
      setErrorSat(err);
      xmlPendienteRef.current = null;
      pdfPendienteRef.current = null;
    },
  });

  const upload = useCargarCfdi(id, {
    onReglasAutorizadas: (r) => {
      setResultado(r);
      setErrorSat(null);
      sat.start();
    },
    onReglasRechazadas: (r) => {
      setResultado(r);
      setErrorSat(null);
      pdfPendienteRef.current = null;
    },
  });

  const esDueno = useEsDueno(sol);
  // Post-envío, o viewer que no es el dueño (Mesa de Control/Auditor/Admin
  // revisando una solicitud ajena · P29) · en ambos casos sin mutaciones.
  const soloLectura = estaPostEnvio(sol.estado) || !esDueno;
  const cfdiCargado = sol.cfdi ?? null;
  // Cadena de subida en curso · upload XML, polling SAT y subida automática del
  // XML+PDF como documentos. Se usa para mostrar el progreso y ocultar acciones.
  const procesoEnCurso = upload.isPending || sat.esperando || subirDocumentos.isPending;

  // Dispara la cadena de validación (reglas → SAT → subida de soporte) con la
  // pareja XML+PDF · compartido por el dropzone (carga inicial) y el botón
  // "Reemplazar CFDI".
  const iniciarValidacion = (xml: File, pdf: File) => {
    setResultado(null);
    setErrorSat(null);
    xmlPendienteRef.current = xml;
    pdfPendienteRef.current = pdf;
    upload.mutate(xml);
  };

  // Handler del input múltiple del botón "Reemplazar CFDI": reparte por
  // extensión, valida la pareja en cliente y, si pasa, arranca la validación.
  const onArchivosReemplazo = async (files: File[]) => {
    const xml = files.find((f) => f.name.toLowerCase().endsWith(".xml")) ?? null;
    const pdf = files.find((f) => f.name.toLowerCase().endsWith(".pdf")) ?? null;
    const v = await validarParejaCfdi(xml, pdf);
    if (!v.ok || !xml || !pdf) {
      toastAdvertencia({
        title: "Archivos inválidos",
        message: v.errorXml || v.errorPdf || v.errorPareja || "Selecciona el XML y el PDF del CFDI (mismo nombre, sólo cambia la extensión).",
      });
      return;
    }
    iniciarValidacion(xml, pdf);
  };

  return (
    <div className="w-full px-4 py-6 md:px-6" style={PASO_PAGE_SHELL}>
      <PageHeader
        title="Cargar CFDI"
        description={`Solicitud ${id.slice(0, 8)}… · subir XML + PDF del CFDI 4.0`}
        breadcrumbs={[
          { label: "Inicio", href: "/dashboard" },
          { label: "Solicitudes", href: "/solicitudes" },
          { label: id.slice(0, 8), href: `/solicitudes/${id}` },
          { label: "Cargar CFDI" },
        ]}
      />
      <LayoutConStepper estado={sol.estado} subtipo={sol.subtipo} tipo={sol.tipo} paso="cfdi" solicitudId={id} contabilizada={sol.enviadaAContabilizacion} fillHeight>
        <Stack gap="md">
          {soloLectura && <ReadOnlyBanner motivo={esDueno ? "post-envio" : "no-dueno"} mensaje="El CFDI cargado no puede reemplazarse." />}

          {/* Ventanas de recepción PUE/PPD vigentes (P41 · D-1) · el usuario ve
              las fechas límite ANTES de cargar (solo cuando puede cargar). */}
          {!soloLectura && <VentanasPuePpdBanner solicitudId={id} />}

          {/* Si ya hay un CFDI cargado, siempre mostramos su detalle + el
              resultado de la validación SAT (el usuario puede previsualizar los
              archivos desde ahí). */}
          {cfdiCargado && <CFDICargadoCard cfdi={cfdiCargado} solicitudId={id} documentos={sol.checklist.documentos} />}

          {/* Carga inicial (aún sin CFDI): dropzone completo con selección
              conjunta o arrastre. */}
          {!soloLectura && !cfdiCargado && (
            <CFDIPdfDropzone cargando={upload.isPending || subirDocumentos.isPending} esperandoSat={sat.esperando} onSubmit={iniciarValidacion} />
          )}

          {/* Con un CFDI ya cargado, el reemplazo es un solo botón que abre el
              diálogo de selección (XML + PDF) y valida automáticamente · sin
              repetir el dropzone (el detalle + preview ya cubren la consulta). */}
          {!soloLectura && cfdiCargado && !procesoEnCurso && (
            <Group>
              <Button variant="default" leftSection={<IconRefresh size={14} />} onClick={() => reemplazarInputRef.current?.click()}>
                Reemplazar CFDI
              </Button>
            </Group>
          )}

          {/* Oculto visualmente pero renderizado (no display:none) · algunos
              navegadores no abren el diálogo nativo al hacer .click() sobre un
              input display:none. */}
          <input
            ref={reemplazarInputRef}
            type="file"
            accept=".xml,application/xml,text/xml,.pdf,application/pdf"
            multiple
            onChange={(e) => {
              void onArchivosReemplazo(Array.from(e.currentTarget.files ?? []));
              e.currentTarget.value = "";
            }}
            className="absolute h-px w-px overflow-hidden opacity-0"
            style={{ pointerEvents: "none" }}
            tabIndex={-1}
            aria-hidden="true"
          />

          {procesoEnCurso && <SatValidacionProgreso />}

          {resultado && !resultado.reglasAutorizadas && <ReglasRechazadasCard errores={resultado.erroresReglas} />}
          {errorSat && <SatErrorCard error={errorSat} />}
        </Stack>
      </LayoutConStepper>
    </div>
  );
}
